import { useEffect, useState } from "react";
import tw from "tailwind-styled-components";
import moment from "moment";
import axios from "axios";
import { MdClose } from "react-icons/md";
import { CircularProgress } from "@mui/material";
import { useMutation } from "react-query";
import Config from "../../Config";
import { ModalHeader, ScrollableContent } from "../Styles/InputStyles";
import DateRangePicker from "../Common/DateRangePicker";
import { maskAccount } from "../../helpers";

/* ================= COMPONENT ================= */

const AnalyticsSideModal = ({ handleClose, token }) => {
    const [showDatePicker, setShowDatePicker] = useState(false);
    const [apiResp, setApiResp] = useState();
    const [apiResp1, setApiResp1] = useState({
        totalCollection: 1240000,
        onlineCollection: 920000,
        offlineCollection: 320000,

        bankWise: {
            online: [
                {
                    bankName: "HDFC",
                    accountNumber: "123456789012",
                    amount: 520000,
                },
                {
                    bankName: "ICICI",
                    accountNumber: "987654321098",
                    amount: 310000,
                },
            ],

            offline: [
                {
                    bankName: "HDFC",
                    accountNumber: "123456789012",
                    amount: 200000,
                },
                {
                    bankName: "ICICI",
                    accountNumber: "987654321098",
                    amount: 150000,
                },
            ],
        },

        paymentModes: [
            { mode: "UPI", amount: 450000 },
            { mode: "Cash", amount: 200000 }
        ],

        paymentGatewaySummary: [
            {
                gateway: "Cashfree",
                collected: 1240000,
                settled: 1080000,
                pendingSettlement: 160000
            },
            {
                gateway: "PayU",
                collected: 820000,
                settled: 790000,
                pendingSettlement: 30000
            }
        ],

        propertyWise: [
            { property: "Kipinn 62", amount: 350000 }
        ],

        expected: 1500000,
        pending: 260000,

        topTenants: [
            { name: "Rahul", amount: 45000 }
        ]
    });
    const [startDate, setStartDate] = useState(
        moment().startOf("month").format("YYYY-MM-DD")
    );
    const [endDate, setEndDate] = useState(
        moment().endOf("month").format("YYYY-MM-DD")
    );

    const fetchFunction = async () => {
        const res = await axios.get(
            `${Config.apiUrl}/transaction/stats?startDate=${startDate}&endDate=${endDate}`,
            {
                headers: { authorization: "Bearer " + token },
            }
        );
        return res.data;
    };

    const { isLoading, mutate, isRefetching } = useMutation(fetchFunction, {
        onSuccess: (res) => setApiResp(res),
    });

    useEffect(() => {
        if (token) mutate();
    }, [startDate, endDate, token]);

    const total = apiResp?.totalCollection || 0;
    const online = apiResp?.onlineCollection || 0;
    const offline = apiResp?.offlineCollection || 0;

    const onlinePercent = total ? Math.round((online / total) * 100) : 0;

    return (
        <Container>
            <ModalHeader>
                <div className="text-white">
                    <h1 className="text-lg font-medium text-white">Collection Analytics</h1>
                    <DateLabel onClick={() => setShowDatePicker(true)}>
                        {moment(startDate).format("DD MMM")} –{" "}
                        {moment(endDate).format("DD MMM")}
                    </DateLabel>
                </div>
                <MdClose color="white" size={22} onClick={handleClose} />
            </ModalHeader>

            {(isLoading || isRefetching) && (
                <LoaderWrapper>
                    <CircularProgress />
                </LoaderWrapper>
            )}

            {!isLoading && !isRefetching && (
                <ScrollableContent className="flex-grow overflow-y-auto max-h-[calc(100dvh-100px)] custom-scrollbar pb-10 scrollbar-hide mt-4 px-4">
                    {/* TOTAL COLLECTION */}
                    <Section>
                        <SectionTitle>Total Collection</SectionTitle>

                        <BigValue>₹{format(total)}</BigValue>

                        <SplitRow>
                            <span>Online</span>
                            <span>₹{format(online)} ({onlinePercent}%)</span>
                        </SplitRow>

                        <SplitRow>
                            <span>Offline</span>
                            <span>₹{format(offline)}</span>
                        </SplitRow>

                        <ProgressTrack>
                            <ProgressBar style={{ width: `${onlinePercent}%` }} />
                        </ProgressTrack>
                    </Section>

                    {/* BANK WISE */}
                    <Section>
                        <SectionTitle>Bank-wise Collection</SectionTitle>

                        {/* ONLINE */}
                        <SubHeader className="text-green-600">
                            Kipinn App (Online)
                        </SubHeader>

                        {apiResp?.onlineCollectionByBank?.length > 0 ? (
                            apiResp.onlineCollectionByBank.map((b, i) => {
                                const percent = total
                                    ? Math.round((b.amount / total) * 100).toFixed(2)
                                    : "0.00";

                                return (
                                    <Item key={`online-${i}`}>
                                        <div className="flex justify-between items-center">
                                            <div>
                                                <p className="text-sm font-medium text-gray-800">
                                                    {b.bankName}
                                                </p>
                                                <p className="text-xs text-gray-400">
                                                    {maskAccount(b.accountNum)}
                                                </p>
                                            </div>

                                            <div className="text-right">
                                                <p className="text-sm font-semibold text-gray-900">
                                                    ₹{format(b.amount)}
                                                </p>
                                                <p className="text-xs text-gray-400">
                                                    {percent}%
                                                </p>
                                            </div>
                                        </div>

                                        <ProgressTrack>
                                            <ProgressBar
                                                style={{
                                                    width: `${percent}%`,
                                                    background:
                                                        "linear-gradient(90deg, #22c55e, #16a34a)",
                                                }}
                                            />
                                        </ProgressTrack>
                                    </Item>
                                );
                            })
                        ) : (
                            <EmptyText>No online collections</EmptyText>
                        )}

                        {/* OFFLINE */}
                        <SubHeader className="text-orange-500 mt-4">
                            Offline (QR / Direct Transfer)
                        </SubHeader>

                        {apiResp?.offlineCollectionByBank?.length > 0 ? (
                            apiResp.offlineCollectionByBank.map((b, i) => {
                                const percent = total
                                    ? Math.round((b.amount / total) * 100).toFixed(2)
                                    : "0.00";

                                return (
                                    <Item key={`offline-${i}`}>
                                        <div className="flex justify-between items-center">
                                            <div>
                                                <p className="text-sm font-medium text-gray-800">
                                                    {b.bankName}
                                                </p>
                                                <p className="text-xs text-gray-400">
                                                    {maskAccount(b.accountNum)}
                                                </p>
                                            </div>

                                            <div className="text-right">
                                                <p className="text-sm font-semibold text-gray-900">
                                                    ₹{format(b.amount)}
                                                </p>
                                                <p className="text-xs text-gray-400">
                                                    {percent}%
                                                </p>
                                            </div>
                                        </div>

                                        <ProgressTrack>
                                            <ProgressBar
                                                style={{
                                                    width: `${percent}%`,
                                                    background:
                                                        "linear-gradient(90deg, #f59e0b, #ea580c)",
                                                }}
                                            />
                                        </ProgressTrack>
                                    </Item>
                                );
                            })
                        ) : (
                            <EmptyText>No offline collections</EmptyText>
                        )}
                    </Section>
                    {apiResp?.paymentGatewaySummary.length > 0 &&
                        <Section>
                            <SectionTitle>Payment Gateway Settlement</SectionTitle>

                            {apiResp?.paymentGatewaySummary?.map((gateway, i) => {
                                const percent = gateway.collected
                                    ? Math.round(
                                        (gateway.settled / gateway.collected) * 100
                                    )
                                    : 0;

                                return (
                                    <Item key={i}>
                                        <div className="flex justify-between mb-2">
                                            <p className="font-medium">
                                                {gateway.gateway}
                                            </p>

                                            <p className="text-sm text-gray-500">
                                                {percent}% Settled
                                            </p>
                                        </div>

                                        <Row>
                                            <span>Collected</span>
                                            <span>
                                                ₹{format(gateway.collected)}
                                            </span>
                                        </Row>

                                        <Row>
                                            <span className="text-green-600">
                                                Settled
                                            </span>
                                            <span className="text-green-600">
                                                ₹{format(gateway.settled)}
                                            </span>
                                        </Row>

                                        <Row>
                                            <span className="text-orange-500">
                                                Pending
                                            </span>
                                            <span className="text-orange-500">
                                                ₹{format(gateway.pending)}
                                            </span>
                                        </Row>

                                        <ProgressTrack>
                                            <ProgressBar
                                                style={{
                                                    width: `${percent}%`,
                                                }}
                                            />
                                        </ProgressTrack>
                                    </Item>
                                );
                            })}
                        </Section>
                    }
                    {/* PAYMENT MODE */}
                    <Section>
                        <SectionTitle>Payment Modes</SectionTitle>

                        {apiResp?.collectionByPaymentMode?.map((m, i) => {
                            const percent = total
                                ? ((m.amount / total) * 100).toFixed(2)
                                : "0.00";

                            return (
                                <div className="flex justify-between items-center py-2 border-b border-gray-100 last:border-0">
                                    <span className="text-sm text-gray-600">{m.mode}</span>
                                    <span className="text-sm font-semibold text-gray-800">
                                        ₹{format(m.amount)}
                                        <span className="text-gray-400 text-xs ml-1">
                                            ({percent}%)
                                        </span>
                                    </span>
                                </div>
                            );
                        })}
                    </Section>

                    {/* PROPERTY WISE */}
                    {/* <Section>
                        <SectionTitle>Property-wise Collection</SectionTitle>

                        {apiResp?.propertyWise?.map((p, i) => (
                            <Row key={i}>
                                <span>{p.property}</span>
                                <span>₹{format(p.amount)}</span>
                            </Row>
                        ))}
                    </Section> */}

                    {/* PENDING */}
                    {/* <Section>
                        <SectionTitle>Pending vs Collected</SectionTitle>

                        <Row>
                            <span>Expected</span>
                            <span>₹{format(apiResp?.expected || 0)}</span>
                        </Row>

                        <Row>
                            <span>Collected</span>
                            <span>₹{format(total)}</span>
                        </Row>

                        <Row>
                            <span className="text-red-500">Pending</span>
                            <span className="text-red-500">
                                ₹{format(apiResp?.pending || 0)}
                            </span>
                        </Row>
                    </Section> */}

                    {/* TOP TENANTS */}
                    {/* <Section>
                        <SectionTitle>Top Paying Tenants</SectionTitle>

                        {apiResp?.topTenants?.map((t, i) => (
                            <Row key={i}>
                                <span>{t.name}</span>
                                <span>₹{format(t.amount)}</span>
                            </Row>
                        ))}
                    </Section> */}
                </ScrollableContent>
            )}

            <DateRangePicker
                show={showDatePicker}
                onClose={() => setShowDatePicker(false)}
                initialStartDate={new Date(startDate)}
                initialEndDate={new Date(endDate)}
                submitHandler={(v) => {
                    setStartDate(moment(v.startDate).format("YYYY-MM-DD"));
                    setEndDate(moment(v.endDate).format("YYYY-MM-DD"));
                }}
            />
        </Container>
    );
};

export default AnalyticsSideModal;

/* ================= HELPERS ================= */

const format = (n) =>
    new Intl.NumberFormat("en-IN").format(n || 0);

/* ================= STYLES ================= */

const Container = tw.div`
  bg-white w-full h-full flex flex-col relative overflow-hidden
`;
const Header = tw.div`flex items-center justify-between bg-primary-color py-4 px-4 text-white`;
//const Header = tw.div`bg-primary-color text-white p-4 flex justify-between`;
const Title = tw.h2`font-semibold text-lg`;
const DateLabel = tw.span`text-xs opacity-80 cursor-pointer`;

//const Section = tw.div`bg-white rounded-xl p-4 shadow-sm mb-4`;
const Section = tw.div`
bg-white/80 backdrop-blur-sm 
border border-gray-200/60 
rounded-xl p-5 
shadow-[0_8px_30px_rgba(0,0,0,0.06)]
mb-4
`;
const SectionTitle = tw.p`text-sm font-semibold text-gray-700 mb-2`;

const BigValue = tw.p`text-2xl font-bold text-gray-800 mb-2`;

const Row = tw.div`flex justify-between text-sm text-gray-600 py-1`;
const SplitRow = tw.div`flex justify-between text-xs text-gray-500`;

const Item = tw.div`mb-3`;
const ItemTop = tw.div`flex justify-between text-sm`;
const Label = tw.span`text-gray-600`;
const Value = tw.span`font-semibold`;

//const ProgressTrack = tw.div`h-2 bg-gray-200 rounded-full mt-1`;
const ProgressTrack = tw.div`
h-2 bg-gray-100 rounded-full mt-2 overflow-hidden
`;
//const ProgressBar = tw.div`h-2 bg-green-500 rounded-full`;
const ProgressBar = tw.div`
h-2 rounded-full 
bg-gradient-to-r from-green-400 to-emerald-500 
transition-all duration-700
`;

const SubHeader = tw.p`
text-xs font-semibold uppercase tracking-wide mb-2
`;

const EmptyText = tw.p`
text-xs text-gray-400 py-2
`;

const LoaderWrapper = tw.div`flex justify-center items-center h-full`;